<template>
  <div class="hierarchy-view">
    <div v-if="isLoading" class="pa-4 text-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2">{{ $i18n.t("Loading groups...") }}</div>
    </div>

    <div v-else-if="rootGroups.length === 0" class="pa-4 text-center grey--text">
      {{ $i18n.t("No groups found") }}
    </div>

    <div v-else class="group-tree">
      <div class="group-tree-header">
        <div class="group-name-col">{{ $i18n.t("Group Name") }}</div>
        <div class="group-type-col">{{ $i18n.t("Type") }}</div>
        <div class="group-client-col">{{ $i18n.t("Client") }}</div>
        <div class="group-contract-col">{{ $i18n.t("Contract") }}</div>
        <div class="group-users-col">{{ $i18n.t("Users") }}</div>
        <div class="group-actions-col"></div>
      </div>

      <div class="group-tree-body">
        <template>
          <group-tree-item
            v-for="(group, index) in filteredRootGroups"
            :key="index"
            :group="group"
            :all-groups="groups"
            :search-term="search"
            :level="0"
            @view="$emit('view', $event)"
            @edit="$emit('edit', $event)"
            @manage-users="$emit('manage-users', $event)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GroupTreeItem from "./GroupTreeItem.vue";

export default {
  name: "GroupHierarchyView",
  components: {
    GroupTreeItem
  },
  props: {
    search: {
      type: String,
      default: ""
    }
  },
  computed: {
    ...mapGetters({
      groups: "group/getGroups",
      isLoading: "group/isLoading",
      error: "group/getError",
      clients: "client/getClients",
      contracts: "contract/getContractsMeta"
    }),
    rootGroups() {
      // Get groups without parents (root groups)
      return this.groups.filter(group => !group.parent);
    },
    filteredRootGroups() {
      if (!this.search) return this.rootGroups;

      // If searching, we need to check if any group in the tree matches
      return this.rootGroups.filter(group => this.groupOrChildrenMatch(group, this.search.toLowerCase()));
    }
  },
  created() {
    this.$store.dispatch("client/fetchClients");
    this.$store.dispatch("contract/fetchContractsMeta");
  },
  methods: {
    groupOrChildrenMatch(group, searchTerm) {
      if (this.groupMatches(group, searchTerm)) return true;

      const client = this.clients.find(c => c._id === group.client);
      if (client && client.name.toLowerCase().includes(searchTerm)) {
        return true;
      }

      const contract = this.contracts.find(c => c._id === group.contract);
      if (contract && contract.name.toLowerCase().includes(searchTerm)) {
        return true;
      }

      const children = this.getChildGroups(group._id);
      return children.some(child => this.groupOrChildrenMatch(child, searchTerm));
    },
    groupMatches(group, searchTerm) {
      return (
        (group.shortName && group.shortName.toLowerCase().includes(searchTerm)) ||
        (group.fullName && group.fullName.toLowerCase().includes(searchTerm)) ||
        (group.type && group.type.toLowerCase().includes(searchTerm))
      );
    },
    getChildGroups(parentId) {
      return this.groups.filter(group => group.parent === parentId);
    }
  }
};
</script>

<style scoped>
.hierarchy-view {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.group-tree {
  width: 100%;
}

.group-tree-header {
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  background-color: #f5f7f9;
}

.group-tree-body {
  max-height: 70vh;
  overflow-y: auto;
}

.group-name-col {
  flex: 3;
  padding-right: 16px;
}

.group-type-col {
  flex: 1;
  min-width: 100px;
  padding-right: 16px;
}

.group-client-col {
  flex: 1;
  min-width: 120px;
  padding-right: 16px;
}

.group-contract-col {
  flex: 1;
  min-width: 120px;
  padding-right: 16px;
}

.group-users-col {
  flex: 1;
  min-width: 100px;
  padding-right: 16px;
}

.group-actions-col {
  flex: 0 0 80px;
  display: flex;
  justify-content: flex-end;
}
</style>
